/**
 * useBoxScore Hook
 * Business logic for the box score modal — groups players by team,
 * sorts by minutes, marks tracked players, and derives game state.
 *
 * Platform-agnostic (no UI). Used by both web and mobile box score modals.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { LeagueKey } from '../league';
import { fuzzyMatchPlayer } from '../playerNameUtils';
import { getTeamAbbr } from '../teams';
import type { FullBoxScoreData, PlayerBoxStats, QuarterScore } from './useLiveStats';
import type { GameStatus } from './useLiveStats.ts';

// =============================================================================
// Types
// =============================================================================

export interface BoxScorePlayerRow {
  playerId: number;
  playerName: string;
  isTracked: boolean;
  min: string;
  pts: number;
  reb: number;
  ast: number;
  fg3m: number;
  fg3a: number;
  fgm: number;
  fga: number;
  ftm: number;
  fta: number;
  stl: number;
  blk: number;
  turnover: number;
  pf: number;
  pra: number;
  pr: number;
  pa: number;
  ra: number;
  bs: number;
}

export interface BoxScoreTeam {
  name: string;
  abbr: string;
  score: number;
  players: BoxScorePlayerRow[];
}

export interface BoxScoreViewModel {
  gameState: GameStatus;
  homeTeam: BoxScoreTeam;
  awayTeam: BoxScoreTeam;
  quarterScores: QuarterScore[];
  quarter: number | null;
  timeRemaining: string | null;
  lastUpdated: number;
}

/** Tracked leg info for display in box score modal */
export interface GameTrackedLeg {
  player: string;
  team: string | null;
  statType: string;
  line: number | null;
  overUnder: string | null;
  prop: string;
  hit: boolean | null;
  actualValue: number | null;
}

// =============================================================================
// Helpers
// =============================================================================

/** Parse minutes string to a numeric value for sorting (e.g., "32:15" → 32.25, "DNP" → -1) */
function parseMinutes(min: string): number {
  if (!min || min === 'DNP' || min === '0' || min === '00:00') return -1;
  // Handle "MM:SS" format
  const parts = min.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0], 10) + parseInt(parts[1], 10) / 60;
  }
  // Handle plain number
  const n = parseFloat(min);
  return Number.isNaN(n) ? -1 : n;
}

/** Convert PlayerBoxStats to BoxScorePlayerRow */
function toPlayerRow(player: PlayerBoxStats, trackedNames: Set<string>): BoxScorePlayerRow {
  const isTracked = Array.from(trackedNames).some((name) =>
    fuzzyMatchPlayer(name, player.playerName),
  );

  return {
    playerId: player.playerId,
    playerName: player.playerName,
    isTracked,
    min: player.min,
    pts: player.pts,
    reb: player.reb,
    ast: player.ast,
    fg3m: player.fg3m,
    fg3a: player.fg3a ?? 0,
    fgm: player.fgm ?? 0,
    fga: player.fga ?? 0,
    ftm: player.ftm ?? 0,
    fta: player.fta ?? 0,
    stl: player.stl,
    blk: player.blk,
    turnover: player.turnover,
    pf: player.pf ?? 0,
    pra: player.pra,
    pr: player.pr,
    pa: player.pa,
    ra: player.ra,
    bs: player.bs,
  };
}

/** Sort players: tracked first, then by minutes DESC */
function sortPlayers(players: BoxScorePlayerRow[]): BoxScorePlayerRow[] {
  return players.sort((a, b) => {
    // Tracked players first
    if (a.isTracked && !b.isTracked) return -1;
    if (!a.isTracked && b.isTracked) return 1;
    // Then by minutes played (DESC)
    return parseMinutes(b.min) - parseMinutes(a.min);
  });
}

// =============================================================================
// Hook
// =============================================================================

export function useBoxScore(
  boxScoreData: FullBoxScoreData | null,
  trackedPlayerNames: string[],
  leagueKey: LeagueKey,
): BoxScoreViewModel | null {
  const trackedNamesSet = useMemo(
    () => new Set(trackedPlayerNames.map((n) => n.toLowerCase())),
    [trackedPlayerNames],
  );

  return useMemo(() => {
    if (!boxScoreData) return null;

    const {
      homeTeam,
      awayTeam,
      homeTeamId,
      awayTeamId,
      homeScore,
      awayScore,
      players,
      quarterScores,
    } = boxScoreData;

    // Group players by team using teamId (primary) or abbreviation (fallback)
    const homeAbbr = getTeamAbbr(homeTeam, leagueKey).toLowerCase();

    const homePlayers: BoxScorePlayerRow[] = [];
    const awayPlayers: BoxScorePlayerRow[] = [];

    for (const player of Object.values(players)) {
      const row = toPlayerRow(player, trackedNamesSet);

      // Primary: match by numeric teamId (exact, no ambiguity)
      if (homeTeamId && player.teamId === homeTeamId) {
        homePlayers.push(row);
      } else if (awayTeamId && player.teamId === awayTeamId) {
        awayPlayers.push(row);
      } else {
        // Fallback: match by abbreviation (handles missing teamId on old cached data)
        const playerAbbr = player.teamName
          ? getTeamAbbr(player.teamName, leagueKey).toLowerCase()
          : '';
        if (playerAbbr === homeAbbr) {
          homePlayers.push(row);
        } else {
          awayPlayers.push(row);
        }
      }
    }

    return {
      gameState: boxScoreData.gameStatus,
      homeTeam: {
        name: homeTeam,
        abbr: getTeamAbbr(homeTeam, leagueKey),
        score: homeScore,
        players: sortPlayers(homePlayers),
      },
      awayTeam: {
        name: awayTeam,
        abbr: getTeamAbbr(awayTeam, leagueKey),
        score: awayScore,
        players: sortPlayers(awayPlayers),
      },
      quarterScores,
      quarter: boxScoreData.quarter,
      timeRemaining: boxScoreData.timeRemaining,
      lastUpdated: boxScoreData.lastUpdated,
    };
  }, [boxScoreData, trackedNamesSet]);
}

// =============================================================================
// On-Court Detection
// =============================================================================

/**
 * Pure comparison: given current players and previous minutes snapshot,
 * returns which players are on the floor (minutes increased) and the
 * new minutes snapshot for next comparison.
 */
export function computeOnCourt(
  players: Record<number, PlayerBoxStats>,
  prevMinutes: Map<number, number>,
): { onCourt: Map<number, boolean>; currentMinutes: Map<number, number> } {
  const currentMinutes = new Map<number, number>();
  const onCourt = new Map<number, boolean>();

  for (const player of Object.values(players)) {
    const currentMin = parseMinutes(player.min);
    currentMinutes.set(player.playerId, currentMin);

    // Only compare if we have previous data (skip first poll)
    if (prevMinutes.size > 0 && prevMinutes.has(player.playerId)) {
      const prevMin = prevMinutes.get(player.playerId)!;
      onCourt.set(player.playerId, currentMin > prevMin && currentMin > 0);
    }
  }

  return { onCourt, currentMinutes };
}

/**
 * Detect which players are currently on the floor during a live game.
 * Compares minutes between consecutive polls — if a player's minutes
 * increased, they're on the court. Returns empty map for non-live games
 * or on the first poll (no previous data to compare).
 */
export function useOnCourtMap(boxScoreData: FullBoxScoreData | null): Map<number, boolean> {
  const prevMinutesRef = useRef<Map<number, number>>(new Map());
  const [onCourtMap, setOnCourtMap] = useState<Map<number, boolean>>(new Map());

  useEffect(() => {
    if (!boxScoreData) {
      prevMinutesRef.current = new Map();
      setOnCourtMap(new Map());
      return;
    }

    if (boxScoreData.gameStatus !== 'live') {
      setOnCourtMap(new Map());
      return;
    }

    const { onCourt, currentMinutes } = computeOnCourt(
      boxScoreData.players,
      prevMinutesRef.current,
    );
    prevMinutesRef.current = currentMinutes;
    setOnCourtMap(onCourt);
  }, [boxScoreData]);

  return onCourtMap;
}
